import * as React from 'react'

import {
  IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarLeftExpand,
} from '@tabler/icons-react'

import { Button } from '@/components/sui/button'
import { useSidebar } from '@/components/ui/sidebar'
import { useComponentTranslation } from '@/hooks/use-component-translation'
import { cn } from '@/lib/utils'

import locales from './locales'

type SidebarTriggerProps = React.ComponentProps<typeof Button>

/**
 * SidebarTrigger wrapper component with cursor-pointer for better UX.
 * Shows PanelLeftIcon when sidebar is open (to close it),
 * and PanelRightIcon when sidebar is collapsed (to open it).
 */
const SidebarTrigger = React.forwardRef<HTMLButtonElement, SidebarTriggerProps>(
  ({ className, onClick, ...props }, ref) => {
    const { t } = useComponentTranslation(locales)
    const { toggleSidebar, state, isMobile, openMobile } = useSidebar()

    const isOpen = isMobile ? openMobile : state === 'expanded'
    const Icon = isOpen
      ? IconLayoutSidebarLeftCollapse
      : IconLayoutSidebarLeftExpand

    return (
      <Button
        ref={ref}
        data-sidebar="trigger"
        data-slot="sidebar-trigger"
        variant="ghost"
        size="icon-sm"
        className={cn('cursor-pointer rounded-full', className)}
        onClick={(event) => {
          onClick?.(event)
          toggleSidebar()
        }}
        {...props}
      >
        <Icon />
        <span className="sr-only">{t('toggleSidebar')}</span>
      </Button>
    )
  },
)
SidebarTrigger.displayName = 'SidebarTrigger'

export { SidebarTrigger, type SidebarTriggerProps }
